import axios from 'axios'
import Vue from 'vue'
import ElementUI from 'element-ui';
import 'element-ui/lib/theme-chalk/index.css';
import router from '@/router'  // 根据实际路径导入路由实例（如果使用 router.push）
import { setWithExpiry, getWithExpiry } from '@/utils/storage-ttl'; // 本地存储加时效性封装文件

let isShowing401Dialog = false;  // 防止多个 401 同时弹窗

Vue.use(ElementUI);

// 全局 loading 相关变量
let loadingInstance = null;
let loadingCount = 0;
let loadingTimer = null; // 延迟展示的定时器
const LOADING_DELAY = 200; // ms，短请求不显示 loading 可避免闪烁

// 显示 loading（带短延迟，使用计数器）
const showLoading = () => {
  // 增加计数
  loadingCount++;

  // 只有在从 0 到 1 的时候才启动延迟展示
  if (loadingCount === 1) {
    // 启动一个定时器，只有在超过延迟才真正显示遮罩
    loadingTimer = setTimeout(() => {
      loadingTimer = null;
      // 防止重复创建
      if (!loadingInstance) {
        loadingInstance = ElementUI.Loading.service({
          fullscreen: true,
          text: '加载中...',
          spinner: 'el-icon-loading',
          background: 'rgba(0,0,0,0.7)'
        });
      }
    }, LOADING_DELAY);
  }
};

// 隐藏 loading（配合计数器与延迟逻辑）
const hideLoading = () => {
  if (loadingCount > 0) {
    loadingCount--;
  }

  // 如果计数归零，取消未触发的定时器或关闭已显示的遮罩
  if (loadingCount === 0) {
    if (loadingTimer) {
      clearTimeout(loadingTimer);
      loadingTimer = null;
    }
    if (loadingInstance) {
      loadingInstance.close();
      loadingInstance = null;
    }
  }
};

// 创建 axios 实例
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API || '/api', // 基础接口地址（推荐从环境变量读取）
  timeout: 50000, // 请求超时时间
  headers: {
    'Content-Type': 'application/json;charset=utf-8' // 默认请求头
  }
})

// ===================== 请求拦截器 =====================
service.interceptors.request.use(
  (config) => {
    showLoading();
    // 1. 请求发送前的处理：比如添加 Token
    var token = null;

    if (getWithExpiry('logInInfo')) {
      token = getWithExpiry('logInInfo').token; // 从本地存储获取 Token
    }
    if (token) {
      config.headers.Authorization = `${token}` // 添加到请求头
    }

    // 2. GET 请求参数处理（可选）
    if (config.method === 'get' && config.params) {
      // 可添加参数过滤、空值处理等逻辑
    }

    return config
  },
  (error) => {
    hideLoading();
    // 请求配置出错时的处理
    console.error('请求配置错误：', error)
    return Promise.reject(error)
  }
)

// ===================== 响应拦截器 =====================
service.interceptors.response.use(
  (response) => {
    hideLoading();

    // 1. 解构响应数据（根据后端返回格式调整）
    const { data } = response

    if (!data || typeof data !== 'object') {
      return data
    }

    const { code, msg, result } = data

    // 2. 兼容不同后端返回格式
    // 2.1 典型：{ code: 200, result: {...} }
    // 2.2 一些接口：{ code: 0, data: {...} }
    // 2.3 无 code 直出：{ data: {...} } 或数组
    if (code === 200 || code === 0 || code === undefined) {
      if (result !== undefined) {
        return result
      }
      if (data.data !== undefined) {
        return data.data
      }
      return data
    } else {
      console.log('业务错误:', code, msg)
      if (code === 401) {
        // 防止多个 401 同时弹窗
        if (!isShowing401Dialog) {
          isShowing401Dialog = true
          Vue.prototype.$confirm('当前尚未登录，是否前往登录?', '提示', {
            confirmButtonText: '去登录',
            cancelButtonText: '取消',
            type: 'warning'
          }).then(() => {
            localStorage.removeItem('logInInfo')
            router.push('/signup/login');
          }).catch(() => {
            // 用户取消，不做跳转
          }).finally(() => {
            // 无论确认还是取消，都重置标志位，以便后续再次触发
            isShowing401Dialog = false
          })
        }
        return false;
      } else {
        // 业务错误：提示错误信息，返回失败 Promise
        Vue.prototype.$message.error(msg || '请求失败') // 需提前安装 element-ui 等 UI 库
        return Promise.reject(new Error(msg || '请求失败'))
      }
    }
  },
  (error) => {
    hideLoading();
    // 3. 网络/系统错误处理
    let errorMsg = ''
    if (error.message.includes('timeout')) {
      errorMsg = '请求超时，请稍后重试'
    } else if (error.message.includes('Network Error')) {
      errorMsg = '网络异常，请检查网络连接'
    } else if (error.response) {
      // 有响应但状态码非 2xx
      const status = error.response.status
      switch (status) {
        case 401:
          errorMsg = '登录失效，请重新登录'
          // 401 时可添加跳转到登录页的逻辑
          localStorage.removeItem('logInInfo')
          this.$router.push('/signup/login');
          break
        case 403:
          errorMsg = '暂无权限访问该资源'
          break
        case 404:
          errorMsg = '请求的资源不存在'
          break
        case 500:
          errorMsg = '服务器内部错误，请稍后重试'
          break
        default:
          errorMsg = `请求错误 [${status}]：${error.response.data?.msg || '未知错误'}`
      }
    } else {
      errorMsg = '请求失败，请稍后重试'
    }

    // 统一提示错误信息
    Vue.prototype.$message.error(errorMsg)
    console.error('请求错误详情：', error)
    return Promise.reject(new Error(errorMsg))
  }
)

// ===================== 封装通用请求方法 =====================
/**
 * GET 请求
 * @param {string} url - 请求地址
 * @param {object} params - 请求参数
 * @returns {Promise}
 */
export const get = (url, params = {}) => {
  return service.get(url, { params })
}

/**
 * POST 请求
 * @param {string} url - 请求地址
 * @param {object} data - 请求体数据
 * @returns {Promise}
 */
export const post = (url, data = {}, config = {}) => {
  let requestUrl = url
  let requestData = data
  let requestConfig = config

  if (typeof url === 'object') {
    const { url: targetUrl, data: body, headers = {}, params = {}, ...rest } = url
    requestUrl = targetUrl
    requestData = body
    requestConfig = { headers, params, ...rest }
  }

  if (requestData instanceof FormData) {
    requestConfig = requestConfig || {}
    requestConfig.headers = {
      ...requestConfig.headers,
      'Content-Type': 'multipart/form-data',
    }
  }

  return service.post(requestUrl, requestData, requestConfig)
}

/**
 * PUT 请求
 * @param {string} url - 请求地址
 * @param {object} data - 请求体数据
 * @returns {Promise}
 */
export const put = (url, data = {}) => {
  return service.put(url, data)
}

/**
 * DELETE 请求
 * @param {string} url - 请求地址
 * @param {object} params - 请求参数
 * @returns {Promise}
 */
export const del = (url, params = {}) => {
  return service.delete(url, { params })
}

// 导出默认实例（备用）
export default service